import _ from 'underscore';
import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Field,
  Radio,
  RadioGroup,
  Dropdown,
  Option,
  Checkbox,
  Link
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { goalHelpIdMapping } from '@bingads-webui-campaign-react/goal-creation-shared-config';
import { GoalPropType, findOptionbyKey } from '@bingads-webui-campaign-react/auto-goal-creation-wizard-utils';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { autoGoalSchemaFieldName, autoGoalSettingSchema } from './auto-goal-schema';
import { getGoalDetailFormStyles } from './styles/goal-details-setting-form-style';
import { LabelHelpButton } from './components/label-help-button';
import { ConversionWindowForm } from './components/window-form';
import { DefaultRevenueForm } from './components/default-revenue-value-form';
import { getAttributeModelOptions, getRevenueOptions } from './constants';
import { ValidatedInput, handleValidationMessages } from './control-components/validated-helper';

export const GoalDetailForm = ({
  draftGoal,
  setDraftGoal,
  setIsFormValid,
  accountName,
  activity,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getGoalDetailFormStyles);
  const serverContext = useContext(ServerContext);
  const locale = _.get(serverContext, ['CurrentLocale'], 'en-us').toLowerCase();
  const languageCode = locale.split('-')[0];

  const onAccountLabel = `${i18n.getString(_TL_('On account:'))} ${accountName}`;
  const acrossAccountsLabel = i18n.getString(_TL_('Across all accounts'));
  const allCountLabel = i18n.getString(_TL_('All (if one ad click causes 3 purchases, 3 conversions will be counted) '));
  const uniqueCountLabel = i18n.getString(_TL_('Unique (if one ad click causes 3 purchases, 1 conversion will be counted)'));

  const setRadioValue = (name, newValue) => {
    const copiedGoal = _.clone(draftGoal);
    if (name === 'IsAccountLevel') {
      copiedGoal[name] = newValue === 'accountLevel';
    } else {
      copiedGoal[name] = newValue;
    }
    if (name === 'IsMainConversionGoal') {
      copiedGoal[name] = newValue === 'yes';
    }
    if (name === 'IsEnhancedConversionsEnabled') {
      copiedGoal[name] = newValue;
      if (activity && activity.trace) {
        activity.trace(`[GoalDetailForm] IsEnhancedConversionsEnabled DraftGoal ${JSON.stringify(copiedGoal)}`);
      }
    }
    setDraftGoal(copiedGoal);
  };

  const onValidate = (result) => {
    if (result) {
      setIsFormValid(result.isValid);
    }
  };

  const onGenericValueChange = (name, value) => {
    const copiedGoal = _.clone(draftGoal);
    copiedGoal[name] = value;
    setDraftGoal(copiedGoal);
  };

  const attributeModelOptions = useMemo(() => getAttributeModelOptions(i18n), [i18n]);
  const selectedAttributeModelOption = findOptionbyKey(attributeModelOptions, draftGoal.AttributionModelType);

  const revenueOptions = useMemo(() => getRevenueOptions(i18n), [i18n]);
  const selectedReveueOption = findOptionbyKey(revenueOptions, draftGoal.GoalValueType);
  const [isValueAvailable, setIsValueAvailable] = useState(draftGoal.GoalValueType === 'VariantValue' || draftGoal.GoalValueType === 'FixedValue');

  const onChangeRevenueSelection = (ev, data) => {
    onGenericValueChange('GoalValueType', data.optionValue || '');
    const shouldShowValueDropdown = data.optionValue === 'FixedValue' || data.optionValue === 'VariantValue';
    setIsValueAvailable(shouldShowValueDropdown);
  };

  return (
    <div className={classes.formBody}>

      <ValidationGroup
        validationProps={{
        i18n,
        schema: autoGoalSettingSchema,
        data: draftGoal,
        onValidate,
      }}
      >
        <ValidatedInput
          fieldProps={{
            className: 'goalName-input',
            label: i18n.getString(_TL_('Goal name')),
          }}
          i18n={i18n}
          style={{ width: '100%' }}
          value={draftGoal.Name}
          onChange={e => onGenericValueChange('Name', e.target.value)}
          validationProps={{
            field: autoGoalSchemaFieldName.Name,
            errorProcessor: errors => handleValidationMessages(i18n, errors),
          }}
        />
        <Field
          label={
            <LabelHelpButton
              i18n={i18n}
              className={classes.labelHelp}
              labelTitle={i18n.getString(_TL_('Revenue value'))}
              helpId={goalHelpIdMapping.Revenue}
            />
          }
        >
          <Dropdown
            className="revenue-dropdown"
            dropdownWidth="auto"
            aria-label={i18n.getString(_TL_('Select the revenue type for each conversion.'))}
            onOptionSelect={onChangeRevenueSelection}
            value={selectedReveueOption && selectedReveueOption.text}
            selectedOptions={selectedReveueOption && [selectedReveueOption.key]}
          >
            {
              revenueOptions.map(option =>
                <Option value={option.key} key={option.key}> {option.text}</Option>)
            }
          </Dropdown>
        </Field>
        {isValueAvailable && (<DefaultRevenueForm
          i18n={i18n}
          draftGoal={draftGoal}
          onChange={onGenericValueChange}
        />)}
        <Field
          className="isMainConversionGoal-radio"
          label={
            <LabelHelpButton
              i18n={i18n}
              className={classes.labelHelp}
              labelTitle={i18n.getString(_TL_('Optimize campaign performance'))}
              helpId={goalHelpIdMapping.OptimizeCampaign}
            />
          }
        >
          <RadioGroup
            onChange={(e, data) => setRadioValue('IsMainConversionGoal', data.value)}
            value={draftGoal.IsMainConversionGoal ? 'yes' : 'no'}
          >
            <Radio value="yes" label={i18n.getString(_TL_('Yes'))} />
            <Radio value="no" label={i18n.getString(_TL_('No'))} />
          </RadioGroup>
        </Field>
        <Field
          className="isEnhancedConversionsEnabled-checkbox"
          label={
            <LabelHelpButton
              i18n={i18n}
              className={classes.labelHelp}
              labelTitle={i18n.getString(_TL_('Enhanced conversions'))}
              helpId={goalHelpIdMapping.EnhancedConversions}
            />
          }
        >
          <Checkbox
            onChange={(e, data) => setRadioValue('IsEnhancedConversionsEnabled', data && data.checked)}
            checked={draftGoal.IsEnhancedConversionsEnabled}
            label={
              <>

                <ReactTemplate
                  model={{
                    terms: text => (<Link href={`https://help.ads.microsoft.com/#apex/ads/${languageCode}/60226/0`} aria-label={text} target="_blank" rel="noopener noreferrer" underline>{text}</Link>),
                    policy: text => (<Link href={`https://privacy.microsoft.com/${locale}/privacystatement`} aria-label={text} target="_blank" rel="noopener noreferrer" underline>{text}</Link>),
                    learnMoreLink: text => (<Link href={`https://help.ads.microsoft.com/#apex/ads/${languageCode}/60178/-1`} aria-label={i18n.getString(_TL_('Learn more about enhanced conversion'))} target="_blank" rel="noopener noreferrer">{text}</Link>),
                  }}
                  template={i18n.getString(_TL_('By enabling enhanced conversion tracking, you agree to Microsoft {{terms}}terms of use{{/terms}} and {{policy}}privacy policy{{/policy}}. {{learnMoreLink}}Learn more{{/learnMoreLink}}.'))}
                />
              </>
            }
          />
        </Field>

        <Accordion collapsible>
          <AccordionItem value="1">
            <AccordionHeader expandIconPosition="end" className={classNames('advanced-accordion', classes.accordionHeader)}>
              {i18n.getString(_TL_('Advanced settings'))}
            </AccordionHeader>
            <AccordionPanel className={classNames(classes.accordionPanel, classes.formBody)}>
              <Field
                className="scope-radio"
                label={
                  <LabelHelpButton
                    i18n={i18n}
                    className={classes.labelHelp}
                    labelTitle={i18n.getString(_TL_('Scope'))}
                    helpId={goalHelpIdMapping.IsAccountLevel}
                  />
               }
              >
                <RadioGroup
                  onChange={(e, data) => setRadioValue('IsAccountLevel', data.value)}
                  value={draftGoal.IsAccountLevel ? 'accountLevel' : 'customerLevel'}
                >
                  <Radio value="accountLevel" label={onAccountLabel} />
                  <Radio value="customerLevel" label={acrossAccountsLabel} />
                </RadioGroup>
              </Field>

              <Field
                className="count-radio"
                label={
                  <LabelHelpButton
                    i18n={i18n}
                    className={classes.labelHelp}
                    labelTitle={i18n.getString(_TL_('Count'))}
                    helpId={goalHelpIdMapping.ConversionCountType}
                  />}
              >
                <RadioGroup
                  onChange={(e, data) => setRadioValue('ConversionCountType', data.value)}
                  value={draftGoal.ConversionCountType}
                >
                  <Radio value="All" label={allCountLabel} />
                  <Radio value="Unique" label={uniqueCountLabel} />
                </RadioGroup>
              </Field>

              <ConversionWindowForm
                i18n={i18n}
                className="lookback-window-form"
                draftGoal={draftGoal}
                onChange={onGenericValueChange}
                windowTargetName={autoGoalSchemaFieldName.LookbackWindowInMinutes}
                schemaFieldGroupName={autoGoalSchemaFieldName.LookbackWindowInMinutesGroup}
                conversionWindowInMin={draftGoal.LookbackWindowInMinutes}
                maxWindowInMin={129600}
                minWindowInMin={0} // Notes: handle differently for isInStoreVisit
                label={
                  <LabelHelpButton
                    i18n={i18n}
                    className={classes.labelHelp}
                    labelTitle={i18n.getString(_TL_('Conversion lookback window'))}
                    helpId={goalHelpIdMapping.LookbackWindowInMinutes}
                  />}
              />

              <ConversionWindowForm
                i18n={i18n}
                className="viwthrough-window-form"
                draftGoal={draftGoal}
                onChange={onGenericValueChange}
                windowTargetName={autoGoalSchemaFieldName.ViewThroughLookbackWindowinMinutes}
                schemaFieldGroupName={autoGoalSchemaFieldName.ViewThroughLookbackWindowinMinutesGroup}
                conversionWindowInMin={draftGoal.ViewThroughLookbackWindowinMinutes}
                maxWindowInMin={43200}
                label={
                  <LabelHelpButton
                    i18n={i18n}
                    className={classes.labelHelp}
                    labelTitle={i18n.getString(_TL_('View-through Conversion window'))}
                    helpId={goalHelpIdMapping.ViewThroughLookbackWindowinMinutes}
                  />}
              />

              <Field
                label={
                  <LabelHelpButton
                    i18n={i18n}
                    className={classes.labelHelp}
                    labelTitle={i18n.getString(_TL_('Conversion attribution model'))}
                    helpId={goalHelpIdMapping.AttributionModelType}
                  />}
              >
                <Dropdown
                  className="attribution-dropdown"
                  aria-label={i18n.getString(_TL_('Select conversion attribution model'))}
                  dropdownWidth="auto"
                  onOptionSelect={(ev, data) => onGenericValueChange('AttributionModelType', data.optionValue || '')}
                  value={selectedAttributeModelOption && selectedAttributeModelOption.text}
                  selectedOptions={selectedAttributeModelOption && [selectedAttributeModelOption.key]}
                >
                  {
                    attributeModelOptions.map(option =>
                      <Option value={option.key} key={option.key}> {option.text}</Option>)
                  }
                </Dropdown>
              </Field>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </ValidationGroup>
    </div>
  );
};

GoalDetailForm.propTypes = {
  accountName: PropTypes.string.isRequired,
  draftGoal: PropTypes.shape(GoalPropType).isRequired,
  setDraftGoal: PropTypes.func.isRequired,
  setIsFormValid: PropTypes.func.isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
};

GoalDetailForm.defaultProps = {
  activity: {
    trace: _.noop,
    error: _.noop,
  },
};
